// React Hook for Activity Timeline Integration
import { useState, useEffect, useCallback, useContext } from 'react';
import ActivityTimelineService, {
  ActivityEvent,
  Achievement,
  UserStats,
  Milestone,
} from '../services/activityTimeline';
import { useAuth } from '../context/AuthContext';
import SpaceContext from '../context/SpaceContext';
import {
  apiService,
  UserActivityFeedItem,
  GamificationAchievementApi,
  GamificationMilestoneApi,
  GamificationUserStatsApi,
} from '../services/api';

function mapFeedItemToActivityEvent(row: UserActivityFeedItem): ActivityEvent {
  const ts = new Date(row.created_at);
  const et = row.event_type;
  let type: ActivityEvent['type'];
  if (et === 'memory_search') {
    type = 'search_performed';
  } else if (et === 'memory_created') {
    type = 'memory_created';
  } else if (et === 'memory_updated') {
    type = 'memory_updated';
  } else if (et === 'memory_deleted') {
    type = 'memory_deleted';
  } else if (et === 'memory_listed') {
    type = 'memory_listed';
  } else if (et === 'memory_fetched') {
    type = 'memory_fetched';
  } else {
    type = 'memory_created';
  }

  const meta = (row.metadata || {}) as Record<string, unknown>;
  const memId = typeof meta.memory_id === 'string' ? meta.memory_id : undefined;
  const resultsCount =
    typeof meta.results_count === 'number' ? meta.results_count : undefined;
  const qFromTitle =
    et === 'memory_search' && row.title.startsWith('Search: ')
      ? row.title.slice('Search: '.length).trim()
      : undefined;

  const impact: ActivityEvent['impact'] =
    et === 'memory_deleted'
      ? 'medium'
      : et === 'memory_search' || et === 'memory_listed' || et === 'memory_fetched'
        ? 'medium'
        : 'high';
  const category: ActivityEvent['category'] =
    et === 'memory_search' || et === 'memory_listed' || et === 'memory_fetched'
      ? 'search'
      : 'creation';

  return {
    id: row.id,
    type,
    title: row.title,
    description: row.integration_type || row.source || '',
    timestamp: ts,
    metadata: {
      spaceId: row.space_id ?? undefined,
      memoryId: memId,
      searchQuery: qFromTitle,
      value: resultsCount,
    },
    impact,
    category,
  };
}

function mapApiAchievement(a: GamificationAchievementApi): Achievement {
  return {
    id: a.id,
    title: a.title,
    description: a.description,
    icon: a.icon,
    category: a.category as Achievement['category'],
    tier: a.tier as Achievement['tier'],
    requirement: {
      type: a.requirement.type,
      value: a.requirement.value,
    },
    progress: a.progress,
    isUnlocked: a.isUnlocked,
    unlockedAt: a.unlockedAt ? new Date(a.unlockedAt) : undefined,
    rarity: a.rarity as Achievement['rarity'],
  };
}

function mapApiMilestone(m: GamificationMilestoneApi): Milestone {
  return {
    id: m.id,
    title: m.title,
    description: m.description,
    icon: m.icon,
    value: m.value,
    isReached: m.isReached,
    reachedAt: m.reachedAt ? new Date(m.reachedAt) : undefined,
    category: m.category as Milestone['category'],
  };
}

function mapApiUserStats(u: GamificationUserStatsApi): UserStats {
  return {
    totalMemories: u.totalMemories,
    totalSearches: u.totalSearches,
    totalSpaces: u.totalSpaces,
    currentStreak: u.currentStreak,
    longestStreak: u.longestStreak,
    timeSpentSaving: u.timeSpentSaving,
    productivityScore: u.productivityScore,
    level: u.level,
    experience: u.experience,
    nextLevelExp: u.nextLevelExp,
    joinedDate: new Date(u.joinedDate),
    lastActiveDate: new Date(u.lastActiveDate),
  };
}

interface UseActivityTimelineReturn {
  // Activities
  recentActivities: ActivityEvent[];
  todayActivities: ActivityEvent[];
  weekActivities: ActivityEvent[];

  // Achievements
  achievements: Achievement[];
  unlockedAchievements: Achievement[];
  achievementsByCategory: (category: Achievement['category']) => Achievement[];

  // Milestones
  milestones: Milestone[];
  reachedMilestones: Milestone[];
  upcomingMilestones: Milestone[];

  // User Stats
  userStats: UserStats;
  activitySummary: {
    todayActivities: number;
    weekActivities: number;
    totalActivities: number;
    currentStreak: number;
    unlockedAchievements: number;
    reachedMilestones: number;
  };

  // Actions
  trackActivity: (
    activity: Omit<ActivityEvent, 'id' | 'timestamp'>,
    opts?: { recordInLocalTimeline?: boolean }
  ) => void;
  trackMemoryCreated: (memoryTitle: string, spaceId?: string, spaceName?: string) => void;
  trackMemoryUpdated: (memoryTitle: string, spaceId?: string, spaceName?: string) => void;
  trackSearchPerformed: (query: string, resultsCount: number) => void;
  trackSpaceCreated: (spaceName: string, spaceId: string) => void;

  // Demo functions
  generateDemoData: () => void;
  clearDemoData: () => void;

  // Loading state
  isLoading: boolean;
  refresh: () => void;
}

export type ActivityFeedScope = 'contextSpace' | 'account';

export interface UseActivityTimelineOptions {
  /** When `account`, activity feed omits space_id (user-wide). Default: current space from context. */
  activityFeedScope?: ActivityFeedScope;
  /** Use local ActivityTimelineService only (Storybook / demo); skips GET /api/v1/gamification. */
  skipServerGamification?: boolean;
}

export const useActivityTimeline = (
  options?: UseActivityTimelineOptions
): UseActivityTimelineReturn => {
  const activityFeedScope = options?.activityFeedScope ?? 'contextSpace';
  const skipServerGamification = options?.skipServerGamification ?? false;
  const { user: _user } = useAuth();
  const spaceCtx = useContext(SpaceContext);
  const spaceId = spaceCtx?.currentSpace?.space_id;
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const activityService = ActivityTimelineService.getInstance();

  const [recentActivities, setRecentActivities] = useState<ActivityEvent[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [activitySummary, setActivitySummary] = useState({
    todayActivities: 0,
    weekActivities: 0,
    totalActivities: 0,
    currentStreak: 0,
    unlockedAchievements: 0,
    reachedMilestones: 0,
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);

      let activitiesFromFeed: ActivityEvent[] = [];
      try {
        const feedParams =
          activityFeedScope === 'account'
            ? { limit: 50, offset: 0 }
            : { space_id: spaceId || undefined, limit: 50, offset: 0 };
        const feed = await apiService.getUserActivityFeed(feedParams);
        activitiesFromFeed = feed.items.map(mapFeedItemToActivityEvent);
        setRecentActivities(activitiesFromFeed);
      } catch (err) {
        console.error('Failed to load server activity feed', err);
        setRecentActivities([]);
        activitiesFromFeed = [];
      }

      const today = new Date().toDateString();
      const todayActivitiesCount = activitiesFromFeed.filter(
        (a) => a.timestamp.toDateString() === today
      ).length;
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekActivitiesCount = activitiesFromFeed.filter(
        (a) => a.timestamp >= weekAgo
      ).length;

      if (skipServerGamification) {
        const allAchievements = activityService.getAchievementProgress();
        const allMilestones = [
          ...activityService.getReachedMilestones(),
          ...activityService.getUpcomingMilestones(),
        ];
        const stats = activityService.getUserStats();
        const summary = activityService.getActivitySummary();
        setAchievements(allAchievements);
        setMilestones(allMilestones);
        setUserStats(stats);
        setActivitySummary(summary);
      } else {
        try {
          const g = await apiService.getGamificationState();
          setAchievements(g.achievements.map(mapApiAchievement));
          setMilestones(g.milestones.map(mapApiMilestone));
          setUserStats(mapApiUserStats(g.userStats));
          setActivitySummary({
            ...g.activitySummary,
            todayActivities: todayActivitiesCount,
            weekActivities: weekActivitiesCount,
          });
        } catch (gamErr) {
          console.error('Failed to load gamification state', gamErr);
          setAchievements([]);
          setMilestones([]);
          setUserStats(null);
          setActivitySummary({
            todayActivities: todayActivitiesCount,
            weekActivities: weekActivitiesCount,
            totalActivities: 0,
            currentStreak: 0,
            unlockedAchievements: 0,
            reachedMilestones: 0,
          });
        }
      }
    } catch (error) {
      console.error('Error loading activity timeline data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activityService, refreshTrigger, spaceId, activityFeedScope, skipServerGamification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const trackActivity = useCallback(
    (
      _activity: Omit<ActivityEvent, 'id' | 'timestamp'>,
      _opts?: { recordInLocalTimeline?: boolean }
    ) => {
      if (skipServerGamification) {
        activityService.trackActivity(_activity, _opts);
      }
      refresh();
    },
    [activityService, refresh, skipServerGamification]
  );

  const trackMemoryCreated = useCallback(
    (_memoryTitle: string, _spaceId?: string, _spaceName?: string) => {
      if (skipServerGamification) {
        activityService.trackActivity(
          {
            type: 'memory_created',
            title: `Created Memory: ${_memoryTitle}`,
            description: `Added new memory "${_memoryTitle}"${_spaceName ? ` in ${_spaceName}` : ''}`,
            impact: 'high',
            category: 'creation',
            metadata: {
              memoryTitle: _memoryTitle,
              spaceId: _spaceId,
              spaceName: _spaceName,
            },
          },
          { recordInLocalTimeline: false }
        );
      }
      refresh();
    },
    [activityService, refresh, skipServerGamification]
  );

  const trackMemoryUpdated = useCallback(
    (memoryTitle: string, spaceId?: string, spaceName?: string) => {
      if (skipServerGamification) {
        activityService.trackActivity(
          {
            type: 'memory_updated',
            title: `Updated Memory: ${memoryTitle}`,
            description: `Modified memory "${memoryTitle}"${spaceName ? ` in ${spaceName}` : ''}`,
            impact: 'medium',
            category: 'creation',
            metadata: {
              memoryTitle,
              spaceId,
              spaceName,
            },
          },
          { recordInLocalTimeline: true }
        );
      }
      refresh();
    },
    [activityService, refresh, skipServerGamification]
  );

  const trackSearchPerformed = useCallback(
    (query: string, resultsCount: number) => {
      if (skipServerGamification) {
        activityService.trackActivity(
          {
            type: 'search_performed',
            title: `Searched: ${query}`,
            description: `Found ${resultsCount} relevant memories`,
            impact: resultsCount > 5 ? 'high' : resultsCount > 2 ? 'medium' : 'low',
            category: 'search',
            metadata: {
              searchQuery: query,
              value: resultsCount,
            },
          },
          { recordInLocalTimeline: false }
        );
      }
      refresh();
    },
    [activityService, refresh, skipServerGamification]
  );

  const trackSpaceCreated = useCallback(
    (spaceName: string, spaceId: string) => {
      if (skipServerGamification) {
        activityService.trackActivity(
          {
            type: 'space_created',
            title: `Created Space: ${spaceName}`,
            description: `Organized memories with new space "${spaceName}"`,
            impact: 'high',
            category: 'organization',
            metadata: {
              spaceName,
              spaceId,
            },
          },
          { recordInLocalTimeline: true }
        );
      }
      refresh();
    },
    [activityService, refresh, skipServerGamification]
  );

  const generateDemoData = useCallback(() => {
    activityService.generateDemoData();
    refresh();
  }, [activityService, refresh]);

  const clearDemoData = useCallback(() => {
    activityService.clearDemoData();
    refresh();
  }, [activityService, refresh]);

  const todayActivities = recentActivities.filter((activity) => {
    const today = new Date().toDateString();
    return activity.timestamp.toDateString() === today;
  });

  const weekActivities = recentActivities.filter((activity) => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return activity.timestamp >= weekAgo;
  });

  const unlockedAchievements = achievements.filter((achievement) => achievement.isUnlocked);

  const achievementsByCategory = useCallback(
    (category: Achievement['category']) => {
      return achievements.filter((achievement) => achievement.category === category);
    },
    [achievements]
  );

  const reachedMilestones = milestones.filter((milestone) => milestone.isReached);
  const upcomingMilestones = milestones.filter((milestone) => !milestone.isReached);

  return {
    recentActivities,
    todayActivities,
    weekActivities,

    achievements,
    unlockedAchievements,
    achievementsByCategory,

    milestones,
    reachedMilestones,
    upcomingMilestones,

    userStats: userStats || {
      totalMemories: 0,
      totalSearches: 0,
      totalSpaces: 1,
      currentStreak: 0,
      longestStreak: 0,
      timeSpentSaving: 0,
      productivityScore: 0,
      level: 1,
      experience: 0,
      nextLevelExp: 100,
      joinedDate: new Date(),
      lastActiveDate: new Date(),
    },
    activitySummary,

    trackActivity,
    trackMemoryCreated,
    trackMemoryUpdated,
    trackSearchPerformed,
    trackSpaceCreated,

    generateDemoData,
    clearDemoData,

    isLoading,
    refresh,
  };
};
